// Phase 4c — dashboard enum SSOT.
//
// Every dashboard enum is declared exactly once here as an `as const`
// tuple, then wrapped by `Sym()` into a registered Zod codec. The tuple
// drives three things from one definition:
//
//   1. the runtime-validating Zod schema (`z.enum`),
//   2. the derived TypeScript union (`(typeof TUPLE)[number]`),
//   3. an `llmDoc` registry entry carrying the LLM-facing type label
//      (and, for value-formatting enums, per-value example docs).
//
// `<ExprInstructions>` (Phase 4c.2) reads the `llmDoc` registry to
// render `value (note) | value | …` hints for each enum-typed kwarg, so
// the LLM sees concrete examples without us hand-writing prose per op.
//
// Scope note: proportion-variant and gauge-threshold enums are
// intentionally absent — Phase 4c ships cartesian + kpi + table only.

import { z } from 'zod';

import type { EnumDomainDoc, EnumDomainResolver } from '../../doc/types.ts';

/**
 * Doc metadata attached to a dashboard enum codec via `llmDoc`.
 * `typeLabel` is the `a | b | c` rendering of the domain; optional
 * `typeNote` adds a one-line hint; `enumValueDocs` maps individual
 * members to a short example string (used for the formatting enums).
 */
export type EnumDoc = {
  readonly typeLabel: string;
  readonly typeNote?: string;
  readonly enumValueDocs?: Readonly<Record<string, string>>;
};

/**
 * Registry of LLM-facing documentation for dashboard enum codecs.
 * Parallels the engine's `opMeta`/`pipelineVerbs`/`aggMeta` registries
 * but keyed by enum schema rather than op schema. Read by the doc
 * walker; never consulted at evaluation time.
 */
export const llmDoc = z.registry<EnumDoc>();

/**
 * Declare an enum once: build the Zod codec from the tuple and register
 * its LLM doc. `typeLabel` defaults to the `a | b | c` join; callers
 * pass `enumValueDocs` / `typeNote` for the value-formatting enums.
 *
 * SSOT helper — every dashboard enum flows through here so the tuple,
 * the runtime codec, the TS union, and the doc entry can never drift.
 */
const Sym = <const T extends readonly [string, ...string[]]>(
  values: T,
  extra: Omit<EnumDoc, 'typeLabel'> & { readonly typeLabel?: string } = {},
): z.ZodEnum<{ readonly [K in T[number]]: K }> => {
  const { typeLabel, ...rest } = extra;
  return z.enum(values).register(llmDoc, { typeLabel: typeLabel ?? values.join(' | '), ...rest });
};

// Series / cartesian enums.

/** Chart accent colours available to series and reference markers. */
export const COLORS = ['blue', 'red', 'green', 'orange', 'purple', 'yellow', 'pink', 'cyan', 'gray'] as const;
export type Color = (typeof COLORS)[number];
export const Color = Sym(COLORS);

/** Line/border stroke styles (series stroke, reference-line style). */
export const STROKES = ['solid', 'dashed', 'dotted'] as const;
export type Stroke = (typeof STROKES)[number];
export const Stroke = Sym(STROKES);

/** Which y-axis a series binds to. */
export const AXIS_SIDES = ['left', 'right'] as const;
export type AxisSide = (typeof AXIS_SIDES)[number];
export const AxisSide = Sym(AXIS_SIDES);

/** Series render type. */
export const SERIES_TYPES = ['line', 'bar', 'area', 'dot'] as const;
export type SeriesType = (typeof SERIES_TYPES)[number];
export const SeriesType = Sym(SERIES_TYPES);

/** Line/area interpolation between adjacent points (line/area series). */
export const CURVES = ['linear', 'smooth', 'step'] as const;
export type Curve = (typeof CURVES)[number];
export const Curve = Sym(CURVES);

/** The semantic domain of a cartesian chart's x-axis. */
export const X_KINDS = ['time', 'category', 'numeric'] as const;
export type XKind = (typeof X_KINDS)[number];
export const XKind = Sym(X_KINDS);

/** Stacking mode for multi-series bar/area charts. */
export const STACKS = ['none', 'stacked', 'percent'] as const;
export type Stack = (typeof STACKS)[number];
export const Stack = Sym(STACKS);

/** Bar/area orientation. */
export const ORIENTATIONS = ['vertical', 'horizontal'] as const;
export type Orientation = (typeof ORIENTATIONS)[number];
export const Orientation = Sym(ORIENTATIONS);

/** Axis a reference-line / reference-area is anchored to. */
export const REF_AXES = ['x', 'y'] as const;
export type RefAxis = (typeof REF_AXES)[number];
export const RefAxis = Sym(REF_AXES);

// Metric (kpi) enums.

/**
 * Metric tile variant. Phase 4c ships `kpi` only — `gauge` (and its
 * `threshold` children) are deferred, so this is a single-member enum
 * today. Kept as an enum (not a literal) so adding `gauge` later is a
 * one-tuple edit with no call-site churn.
 */
export const METRIC_VARIANTS = ['kpi'] as const;
export type MetricVariant = (typeof METRIC_VARIANTS)[number];
export const MetricVariant = Sym(METRIC_VARIANTS);

/** Numeric value formatting for a metric tile. */
export const NUM_FORMATS = ['compact', 'decimal', 'integer', 'percent', 'currency'] as const;
export type NumFormat = (typeof NUM_FORMATS)[number];
export const NumFormat = Sym(NUM_FORMATS, {
  typeNote: 'how the metric value is rendered',
  enumValueDocs: {
    compact: '1.2k, 3.4M',
    decimal: '1,234.56',
    integer: '1,235',
    percent: '12.3%',
    currency: '$1,234',
  },
});

// Table enums.

/** Horizontal alignment of a table column's cells. */
export const ALIGNS = ['left', 'center', 'right'] as const;
export type Align = (typeof ALIGNS)[number];
export const Align = Sym(ALIGNS);

/** Per-column value formatting in a table tile. */
export const COLUMN_FORMATS = [
  'text',
  'number',
  'percent',
  'currency',
  'datetime',
  'duration-ms',
  'sparkline',
] as const;
export type ColumnFormat = (typeof COLUMN_FORMATS)[number];
export const ColumnFormat = Sym(COLUMN_FORMATS, {
  typeNote: 'how each cell in the column is rendered',
  enumValueDocs: {
    text: 'raw string',
    number: '1,234.56',
    percent: '12.3%',
    currency: '$1,234',
    datetime: '2024-01-15 14:30',
    'duration-ms': '1m 23s',
    sparkline: 'inline mini-chart over a nested series cell',
  },
});

/**
 * Canonical (name, codec, members) list of every dashboard enum.
 * SSOT for: the parametrized codec tests, and the `<ExprInstructions>`
 * enum-domain glossary. Adding an enum is one row here.
 */
export const ENUM_CODECS: readonly {
  readonly name: string;
  readonly codec: z.ZodEnum;
  readonly values: readonly string[];
}[] = [
  { name: 'Color', codec: Color, values: COLORS },
  { name: 'Stroke', codec: Stroke, values: STROKES },
  { name: 'AxisSide', codec: AxisSide, values: AXIS_SIDES },
  { name: 'SeriesType', codec: SeriesType, values: SERIES_TYPES },
  { name: 'Curve', codec: Curve, values: CURVES },
  { name: 'XKind', codec: XKind, values: X_KINDS },
  { name: 'Stack', codec: Stack, values: STACKS },
  { name: 'Orientation', codec: Orientation, values: ORIENTATIONS },
  { name: 'RefAxis', codec: RefAxis, values: REF_AXES },
  { name: 'MetricVariant', codec: MetricVariant, values: METRIC_VARIANTS },
  { name: 'NumFormat', codec: NumFormat, values: NUM_FORMATS },
  { name: 'Align', codec: Align, values: ALIGNS },
  { name: 'ColumnFormat', codec: ColumnFormat, values: COLUMN_FORMATS },
];

/**
 * Build the `<ExprInstructions enumDomains>` glossary from the
 * `llmDoc` registry. SSOT: pulls `typeLabel` / `enumValueDocs`
 * straight from each codec's registered doc — no hand-maintained
 * second copy of the value examples.
 */
export const dashboardEnumDomains = (): readonly EnumDomainDoc[] =>
  ENUM_CODECS.map(({ name, codec }) => {
    const doc = llmDoc.get(codec);
    return {
      name,
      typeLabel: doc?.typeLabel ?? '',
      ...(doc?.enumValueDocs === undefined ? {} : { valueDocs: doc.enumValueDocs }),
    };
  });
/**
 * Member-tuple → domain-name lookup, derived from the `ENUM_CODECS`
 * SSOT. Keyed by the joined member list rather than schema identity:
 * op kwargs declare a plain `z.enum(COLORS)` — a *fresh* schema object,
 * never the registered `Color` codec — so an identity lookup would
 * always miss. Member sets are distinct across every dashboard enum, so
 * the join is unambiguous.
 */
const DOMAIN_BY_MEMBERS: ReadonlyMap<string, string> = new Map(
  ENUM_CODECS.map(({ name, values }) => [values.join(' '), name] as const),
);

/**
 * Resolve an enum's ordered member list to its dashboard domain name
 * (e.g. `['blue','red',…]` → `Color`). Threaded into the doc
 * Zod-walker so enum-typed kwargs render as a `Color` reference; the
 * values themselves are expanded once in the "Enum domains" glossary.
 */
export const enumDomainNameOf: EnumDomainResolver = (members) => DOMAIN_BY_MEMBERS.get([...members].join(' '));
